import {
  AvailableLoadsByCompanyInsight,
  AvailableLoadsByRouteInsight,
  AvailableLoadsByTimeInsight,
  AvailableLoadsInsight,
  ContainerDNUStatisticsByTimeInsights,
  CriticalStatusContainersInsight,
  DeliveriesToNonPrimarySiteByCompanyInsight,
  ExceedsNonPrimaryVolumeByTimeInsight,
  FormattedDateTime,
  HaulMethodSegmentedControl,
  HighNonPrimaryVolumeContainersInsight,
  InsightsOverviewGrid,
  LoadRejectInsight,
  LoadsByCompanyInsight,
  LoadsByTimeInsights,
  RejectsByCompanyInsights,
  RejectsByTimeInsights,
  ResizeableContent,
  ThemedPage,
  useApp,
  VolumeInventoryByTimeInsights,
  WarningStatusContainersInsight
} from "@ots-frontend/ots-ui";
import {Card, Container, Grid, Group, Menu, Radio, rem, ScrollArea, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown, IconDeviceAnalytics} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
  cargoSubtype,
  endOfDay,
  startOfDay,
  subDays,
  toTransportMethod,
  transportCategory,
  transportMethod,
  volumeInventoryVolumeType
} from "@ots-frontend/ots-data";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppCategory} from "../../store/ui/selectors";
import {useNavigate} from "react-router-dom";
import {useGetRecentInsights} from "../../components/hooks/useGetRecentInsights";
import {getInsightsDisposalCostType, getInsightsHaulMethod} from "../../store/insights/selectors";
import {useDispatch} from "react-redux";
import {setInsightsDisposalCostType, setInsightsHaulMethod} from "../../store/insights/actions";
import {isInsightsDisposalCostType} from "../../store/insights/model/insights-disposal-cost-type";

export const InsightsPage = () => {

  const navigate = useNavigate()
  const {t} = useTranslation(["translation", "common"]);
  const dispatch = useDispatch()

  const {regionId} = useApp()
  const appCategory = useAppSelector(getAppCategory);
  const selectedHaulMethod = useAppSelector(getInsightsHaulMethod)
  const disposalCostType = useAppSelector(getInsightsDisposalCostType)

  const [rateModels, setRateModels] = useState<string[] | undefined>(undefined);

  const filter = useMemo(() => ({
    fromDttm: startOfDay(subDays(new Date(), 4)).getTime(),
    toDttm: endOfDay(new Date()).getTime()
  }), [])

  const insights = useGetRecentInsights({
    fromDttm: filter.fromDttm,
    toDttm: filter.toDttm,
    regionId: regionId,
    category: appCategory,
  })

  const {
    statistics: {
      data: statistics,
      isLoading: isStatisticsLoading,
      refetch: refetchStatistics
    }
  } = insights;

  const regionStats = useMemo(() => {
    return regionId ? statistics?.getRegionStats(regionId) : undefined
  }, [regionId, statistics])

  const {
    volumeInventories: {
      data: volumeInventorySnapshots,
      isLoading: isVolumeInventoryLoading,
      refetch: refetchVolumeInventory
    }
  } = insights;

  const {
    allocatableLoads: {
      data: allocatableLoadSnapshots,
      isLoading: isAllocatableLoadsLoading,
      refetch: refetchAllocatableLoads
    }
  } = insights;

  const {
    containerDNUStatistics: {
      data: containerDnuSnapshots,
      isLoading: isContainerDnuStatisticsLoading,
      refetch: refetchContainerDnuStatistics
    }
  } = insights;

  const {
    exceedsNonPrimaryVolumeStatistics: {
      data: exceedsNonPrimaryVolumeSnapshots,
      isLoading: isExceedsNonPrimaryVolumeStatisticsLoading,
      refetch: refetchExceedsNonPrimaryVolumeStatistics
    }
  } = insights;

  const {
    containerStatus: {
      availableLoads,
      availableLoadsBy,
      groupBy: groupByContainerStatuses,
      isLoading: isContainerStatusLoading,
      isFetching: isContainerStatusFetching,
      refetch: containerStatusRefetch
    }
  } = insights;

  const onRefreshClick = useCallback(() => {
    if (refetchStatistics) {
      refetchStatistics()
    }

    if (containerStatusRefetch) {
      containerStatusRefetch()
    }

    if (refetchVolumeInventory) {
      refetchVolumeInventory()
    }

    if (refetchAllocatableLoads) {
      refetchAllocatableLoads()
    }

    if (refetchContainerDnuStatistics) {
      refetchContainerDnuStatistics()
    }

    if (refetchExceedsNonPrimaryVolumeStatistics) {
      refetchExceedsNonPrimaryVolumeStatistics()
    }
  }, [refetchStatistics, containerStatusRefetch, refetchVolumeInventory, refetchAllocatableLoads, refetchContainerDnuStatistics, refetchExceedsNonPrimaryVolumeStatistics])

  const onHaulMethodChange = useCallback((value: string) => {
    dispatch(setInsightsHaulMethod(value === 'all' ? undefined : toTransportMethod(value)))
  }, [dispatch])

  const onDisposalCostTypeChange = useCallback((value: string) => {
    if (isInsightsDisposalCostType(value)) {
      dispatch(setInsightsDisposalCostType(value))
    }
  }, [dispatch])

  useEffect(() => {
    if (disposalCostType === 'includeDisposalCosts') {
      setRateModels(["Hauling", "Pipeline", "Disposal"])
    } else if (disposalCostType === 'onlyDisposalCosts') {
      setRateModels(["Disposal"])
    } else {
      setRateModels(undefined)
    }
  }, [disposalCostType])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <ScrollArea h={size.height} p="sm" offsetScrollbars={true}>
                <Card shadow="sm" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group spacing="xs">
                      <IconDeviceAnalytics/>
                      <Title order={4}>
                        {t("page.site.insights.title", "Insights", {ns: "translation"})}
                      </Title>
                    </Group>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              Actions
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => onRefreshClick()}>
                          {t("commons.action.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <Menu.Divider/>
                        <Menu.Item onClick={() => navigate("search")}>
                          {t("commons.shared.historical", "Historical", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <Title order={6}>
                    {t("page.site.insights.subtitle", "Recent Insights", {ns: "translation"})}
                    {" -  From "}
                    <FormattedDateTime date={filter.fromDttm}/>
                  </Title>
                  <Group pt="md" position="apart">
                    <HaulMethodSegmentedControl
                      defaultValue={selectedHaulMethod ? selectedHaulMethod.code : 'all'}
                      onChange={onHaulMethodChange}
                    />
                    <Radio.Group value={disposalCostType} onChange={onDisposalCostTypeChange}>
                      <Group mt="xs">
                        <Radio value="excludeDisposalCosts" label="Exclude Offload Costs"/>
                        <Radio value="includeDisposalCosts" label="Include Offload Costs"/>
                        <Radio value="onlyDisposalCosts" label="Only Offload Costs"/>
                      </Group>
                    </Radio.Group>
                  </Group>
                </Card>

                <InsightsOverviewGrid
                  insights={regionStats}
                  category={appCategory}
                  loading={isStatisticsLoading}
                  method={selectedHaulMethod}
                  rateModels={rateModels}>
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <>
                      <Grid.Col span={4}>
                        <AvailableLoadsInsight
                          value={availableLoads}
                          loading={isContainerStatusLoading || isContainerStatusFetching}
                          showSearch={true}/>
                      </Grid.Col>
                    </>
                  }
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <Grid.Col span={6}>
                      <AvailableLoadsByCompanyInsight
                        id={"recent_avail_loads_by_company_chart"}
                        data={availableLoadsBy}
                        loading={isContainerStatusLoading || isContainerStatusFetching}
                        showSearch={true}
                        containerProps={{
                          h: 450
                        }}/>
                    </Grid.Col>
                  }
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <Grid.Col span={6}>
                      <AvailableLoadsByRouteInsight
                        id={"recent_avail_loads_by_route_chart"}
                        data={availableLoadsBy}
                        loading={isContainerStatusLoading || isContainerStatusFetching}
                        showSearch={true}
                        containerProps={{
                          h: 450
                        }}/>
                    </Grid.Col>
                  }
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <Grid.Col span={6}>
                      <AvailableLoadsByTimeInsight
                        id={"recent_volume_inventory_by_time_chart"}
                        fromDttm={filter.fromDttm}
                        toDttm={filter.toDttm}
                        data={allocatableLoadSnapshots}
                        category={appCategory}
                        loading={isAllocatableLoadsLoading}
                        showSearch={true}
                        stacked={{
                          enabled: true,
                          filled: true
                        }}/>
                    </Grid.Col>
                  }
                  <Grid.Col span={6}>
                    <VolumeInventoryByTimeInsights
                      id={"recent_volume_inventory_by_time_chart"}
                      fromDttm={filter.fromDttm}
                      toDttm={filter.toDttm}
                      snapshots={volumeInventorySnapshots}
                      hauledStats={regionStats}
                      category={appCategory}
                      loading={isVolumeInventoryLoading || isStatisticsLoading}
                      types={[volumeInventoryVolumeType.usable, volumeInventoryVolumeType.unusable]}
                      splitTypes={true}
                      methods={selectedHaulMethod ? [selectedHaulMethod] : undefined}
                      splitMethods={true}/>
                  </Grid.Col>
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <Grid.Col span={6}>
                      <LoadsByTimeInsights
                        id={"recent_loads_by_time_chart"}
                        fromDttm={filter.fromDttm}
                        toDttm={filter.toDttm}
                        data={statistics?.truckCompanyStats}
                        category={appCategory}
                        loading={isStatisticsLoading}
                        showSearch={true}
                        stacked={{
                          enabled: true,
                          filled: true
                        }}
                        containerProps={{
                          h: 400
                        }}/>
                    </Grid.Col>
                  }
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <Grid.Col span={6}>
                      <LoadsByCompanyInsight
                        id={"recent_loads_by_company_chart"}
                        data={statistics}
                        category={appCategory}
                        loading={isStatisticsLoading}
                        showSearch={true}
                        containerProps={{
                          h: 400
                        }}/>
                    </Grid.Col>
                  }
                  {(appCategory === transportCategory.oil && (!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking)) &&
                    <Grid.Col span={6}>
                      <DeliveriesToNonPrimarySiteByCompanyInsight
                        id={"recent_deliveries_to_non_primary_site_by_company_chart"}
                        data={statistics}
                        category={appCategory}
                        loading={isStatisticsLoading}
                        containerProps={{
                          h: 400
                        }}/>
                    </Grid.Col>
                  }
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <Grid.Col span={6}>
                      <CriticalStatusContainersInsight
                        value={groupByContainerStatuses.critical}
                        loading={isContainerStatusLoading || isContainerStatusFetching}/>
                    </Grid.Col>
                  }
                  {((!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) && groupByContainerStatuses.warningHaulable.length > 0) &&
                    <Grid.Col span={12}>
                      <WarningStatusContainersInsight
                        category={appCategory}
                        value={groupByContainerStatuses.warningHaulable}
                        loading={isContainerStatusLoading || isContainerStatusFetching}/>
                    </Grid.Col>
                  }
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <Grid.Col span={6}>
                      <ExceedsNonPrimaryVolumeByTimeInsight
                        id={"recent_exceed_non_primary_volume_by_time_chart"}
                        fromDttm={filter.fromDttm}
                        toDttm={filter.toDttm}
                        data={exceedsNonPrimaryVolumeSnapshots}
                        category={appCategory}
                        loading={isExceedsNonPrimaryVolumeStatisticsLoading}
                        containerProps={{
                          h: 400
                        }}
                        showSearch={true}/>
                    </Grid.Col>
                  }
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <Grid.Col span={6}>
                      <HighNonPrimaryVolumeContainersInsight
                        cargoSubtype={appCategory === transportCategory.water ? cargoSubtype.oil : cargoSubtype.water}
                        value={groupByContainerStatuses.highNonPrimaryVolume}
                        loading={isContainerStatusLoading || isContainerStatusFetching}/>
                    </Grid.Col>
                  }
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <Grid.Col span={6}>
                      <RejectsByTimeInsights
                        id={"recent_rejects_by_time_chart"}
                        fromDttm={filter.fromDttm}
                        toDttm={filter.toDttm}
                        data={statistics?.truckCompanyStats}
                        category={appCategory}
                        loading={isStatisticsLoading}
                        showSearch={true}
                        stacked={{
                          enabled: true,
                          filled: true
                        }}
                        containerProps={{
                          h: 400
                        }}/>
                    </Grid.Col>
                  }
                  {(!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking) &&
                    <Grid.Col span={6}>
                      <RejectsByCompanyInsights
                        id={"recent_rejects_by_company_chart"}
                        statistics={statistics}
                        category={appCategory}
                        loading={isStatisticsLoading}
                        containerProps={{
                          h: 400
                        }}/>
                    </Grid.Col>
                  }
                  {(appCategory === transportCategory.oil && statistics && (!selectedHaulMethod || selectedHaulMethod === transportMethod.trucking)) &&
                    <>
                      {statistics.truckCompanyStats.map((stat) => {
                        return (
                          <Grid.Col span={4}>
                            <LoadRejectInsight
                              id={stat.id + "_recent_load_rejects_chart"}
                              statistics={stat}
                              category={appCategory}
                              loading={isStatisticsLoading}
                              containerProps={{
                                h: 400
                              }}/>
                          </Grid.Col>
                        )
                      })}
                    </>
                  }
                  <Grid.Col span={6}>
                    <ContainerDNUStatisticsByTimeInsights
                      id={"recent_container_dnu_by_time_chart"}
                      fromDttm={filter.fromDttm}
                      toDttm={filter.toDttm}
                      snapshots={containerDnuSnapshots}
                      category={appCategory}
                      loading={isContainerDnuStatisticsLoading}
                      methods={selectedHaulMethod ? [selectedHaulMethod] : undefined}
                      splitMethods={true}
                      containerProps={{
                        h: 400
                      }}/>
                  </Grid.Col>
                </InsightsOverviewGrid>
              </ScrollArea>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
